import React from "react";
import { Heart, Star, Award, X } from "lucide-react";

const AdmireModal = ({
  showAdmireModal,
  setShowAdmireModal,
  admireOptions,
  handleAdmireClick,
  isAdmiring,
  currentAdmireType,
  userName,
}) => {
  if (!showAdmireModal) return null;

  return (
    <>
      <div
        className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-end md:items-center justify-center z-50"
        onClick={() => setShowAdmireModal(false)}
      >
        <div
          className="bg-white w-full md:max-w-md md:w-full shadow-2xl rounded-t-3xl md:rounded-2xl animate-slideUp md:animate-fadeIn"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Modal Header */}
          <div className="p-4 border-b border-gray-100">
            {/* Mobile: Drag Handle */}
            <div className="md:hidden flex justify-center mb-3">
              <div className="w-12 h-1.5 bg-gray-300 rounded-full"></div>
            </div>

            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-xl md:text-2xl font-bold text-gray-900">
                  Choose Admiration Type
                </h3>
                <p className="text-xs md:text-sm text-gray-500 mt-1">
                  How do you admire {userName}?
                </p>
              </div>
              {/* Desktop: Close Button */}
              <button
                onClick={() => setShowAdmireModal(false)}
                className="hidden md:flex w-10 h-10 rounded-full hover:bg-gray-200 items-center justify-center text-black hover:text-gray-700 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Modal Body */}
          <div className="p-4 max-h-[60vh] md:max-h-none overflow-y-auto">
            <div className="space-y-3">
              {admireOptions.map((option, index) => {
                const isDisabled =
                  isAdmiring && currentAdmireType !== option.value;
                const isSelected =
                  isAdmiring && currentAdmireType === option.value;

                return (
                  <button
                    key={index}
                    onClick={() => handleAdmireClick(option)}
                    disabled={isDisabled}
                    className={`
                      w-full rounded-xl text-left group transition-colors p-1 md:p-0
                      ${
                        isSelected
                          ? "bg-pink-50 border-2 border-pink-400"
                          : isDisabled
                          ? "bg-gray-50 opacity-50 cursor-not-allowed"
                          : "active:bg-gray-50 hover:bg-gray-50"
                      }
                    `}
                    title={
                      isDisabled
                        ? "Remove your current admire first to change type"
                        : ""
                    }
                  >
                    <div className="flex items-start gap-4">
                      {/* Icon */}
                      <div
                        className={`flex-shrink-0 w-10 h-10 md:w-12 md:h-12 rounded-full bg-gradient-to-br from-pink-100 to-red-100 flex items-center justify-center ${
                          !isDisabled ? "group-hover:scale-110" : ""
                        } transition-transform`}
                      >
                        {React.createElement(option.icon, {
                          className: "w-5 h-5 md:w-6 md:h-6 text-gray-700",
                        })}
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="text-base md:text-lg font-semibold text-gray-900">
                            {option.label}
                          </h4>
                          {isSelected && (
                            <span className="text-xs bg-pink-100 text-pink-700 px-2 py-0.5 rounded-full">
                              Selected
                            </span>
                          )}
                          {isDisabled && (
                            <span className="text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded-full">
                              Remove first
                            </span>
                          )}
                        </div>
                        <p
                          className={`text-xs md:text-sm ${
                            isDisabled ? "text-gray-400" : "text-gray-600"
                          } italic flex items-center gap-1 flex-wrap`}
                        >
                          {option.desc.split(" ").map((word, idx) => {
                            if (word === "❤️") {
                              return (
                                <Heart
                                  key={idx}
                                  className="w-3 h-3 md:w-3.5 md:h-3.5 text-gray-500 inline"
                                />
                              );
                            } else if (word === "😉") {
                              return (
                                <span key={idx} className="text-gray-500">
                                  😉
                                </span>
                              );
                            } else if (word === "✨") {
                              return (
                                <Star
                                  key={idx}
                                  className="w-3 h-3 md:w-3.5 md:h-3.5 text-gray-500 inline"
                                />
                              );
                            } else if (word === "🔥") {
                              return (
                                <Award
                                  key={idx}
                                  className="w-3 h-3 md:w-3.5 md:h-3.5 text-gray-500 inline"
                                />
                              );
                            }
                            return <span key={idx}>{word} </span>;
                          })}
                        </p>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Modal Footer */}
          <div className="p-2 border-t border-gray-100 bg-gray-50 rounded-b-3xl md:rounded-b-2xl">
            <p className="text-xs text-gray-500 text-center">
              Your admiration type will be visible to {userName}
            </p>
          </div>

          {/* Mobile: Safe area padding for iPhone notches */}
          <div className="md:hidden h-safe-area-inset-bottom"></div>
        </div>
      </div>
      <style jsx>{`
        /* Desktop: Fade and scale animation */
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: scale(0.95);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }

        .animate-fadeIn {
          animation: fadeIn 0.2s ease-out;
        }

        /* Mobile: Slide up from bottom animation (Instagram style) */
        @keyframes slideUp {
          from {
            transform: translateY(100%);
          }
          to {
            transform: translateY(0);
          }
        }

        .animate-slideUp {
          animation: slideUp 0.3s cubic-bezier(0.32, 0.72, 0, 1);
        }

        /* Prevent body scroll when modal is open */
        body:has(.z-50) {
          overflow: hidden;
        }
      `}</style>
    </>
  );
};

export default AdmireModal;
